import { useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { trim } from 'lodash';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuthContext } from 'src/auth/hooks';
import { useAlert } from 'src/components/alert';
import Breadcrumbs from 'src/components/custom-breadcrumbs';
import { LoadingScreen } from 'src/components/loading-screen';
import { useOrganizationContext } from 'src/context/organization';
import {
  CREATE_ORG_SUMMARY_TAGS,
  DELETE_ORG_SUMMARY_TAGS,
  GET_ORG_SUMMARY_TAGS,
  GET_SUMMARY_TAGS,
} from 'src/graphql/c_tags/queries';
import BulkAddTagsDialog from 'src/sections/manager/tags/bulk-add-tags-dialog';
import { OrganizationSelect } from 'src/sections/superadmin/components/organization-select/OrganizationSelect';

export default function TagManagerPage() {
  const { user } = useAuthContext();
  const { selectedOrganizationId } = useOrganizationContext();
  const userRole = user?.users_user_roles?.[0]?.user_roles_roles?.code;

  const alert = useAlert();
  const [selectedOrganization, setSelectedOrganization] = useState();
  const { data: summaryTagsData } = useQuery(GET_SUMMARY_TAGS);
  const {
    data: orgSummaryTags,
    loading: orgSummaryTagsLoading,
    refetch: refetchOrgSummaryTags,
  } = useQuery(GET_ORG_SUMMARY_TAGS, {
    variables: {
      organization_id: selectedOrganization,
    },
  });
  const tagsUnderSelectedOrganization = orgSummaryTags?.rows?.filter(
    (row) => row?.organization_id === selectedOrganization
  );

  const [createSummaryTag] = useMutation(CREATE_ORG_SUMMARY_TAGS);
  const [deleteSummaryTags] = useMutation(DELETE_ORG_SUMMARY_TAGS);

  const [tagName, setTagName] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);

  useEffect(() => {
    setSelectedOrganization(selectedOrganizationId);
  }, [selectedOrganizationId]);

  const onSaveTag = async () => {
    const tagTitle = trim(tagName);
    setLoading(true);
    setTagName('');
    await createSummaryTag({
      variables: {
        data: {
          organization_id: selectedOrganization,
          title: tagTitle,
        },
      },
    });

    refetchOrgSummaryTags();
    setLoading(false);
  };

  const onDeleteTags = async () => {
    alert.confirm('Are you sure you want to delete the selected tags?').then(() => {
      setLoading(true);
      deleteSummaryTags({
        variables: {
          tagIds: selectedTags,
        },
      }).finally(() => {
        setSelectedTags([]);
        setLoading(false);
        refetchOrgSummaryTags();
      });
    });
  };

  return (
    <Container>
      <Helmet>
        <title>Tag Management</title>
      </Helmet>
      <Breadcrumbs heading="Tag Management" sx={{ mb: { xs: 3, md: 5 } }} />
      {userRole === 'superadmin' && (
        <OrganizationSelect
          selectedOrganization={selectedOrganization}
          setSelectedOrganization={setSelectedOrganization}
        />
      )}

      {selectedOrganization ? (
        <>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography sx={{ mb: 4 }} fontWeight={600}>
                {'Summary Tags'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <Grid container spacing={2}>
                  {summaryTagsData?.rows?.map((tag) => {
                    return (
                      <Grid key={tag?.c_tag_id} item md={6} lg={4} xl={3}>
                        <FormControlLabel
                          disabled
                          label={tag?.title}
                          control={<Checkbox checked />}
                        />
                      </Grid>
                    );
                  })}
                  <Grid item md={6} lg={4} xl={3}>
                    <FormControlLabel
                      disabled
                      label={'Do Not Contact'}
                      control={<Checkbox checked />}
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2, position: 'relative' }}>
            {(orgSummaryTagsLoading || loading) && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  backgroundColor: 'rgba(255,255,255,0.5)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 1,
                }}
              >
                <LoadingScreen />
              </Box>
            )}
            <CardContent>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box>
                  <Typography fontWeight={600}>{'Organization Tags'}</Typography>
                  <Typography variant={'body2'} sx={{ mb: 4 }}>
                    These are tags you manage within your organization
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <TextField
                  fullWidth
                  label={'Enter a tag'}
                  placeholder='Type a tag and press enter or click the "Add" button'
                  onChange={(e) => setTagName(e?.target?.value)}
                  value={tagName}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      onSaveTag();
                    }
                  }}
                />
                <Button variant={'contained'} sx={{ ml: 1 }} onClick={onSaveTag}>
                  Add
                </Button>
                {userRole === 'superadmin' && (
                  <Button
                    variant={'outlined'}
                    sx={{ ml: 1, flexShrink: 0 }}
                    onClick={() => setBulkAddOpen(true)}
                  >
                    Add multiple
                  </Button>
                )}
              </Box>
              <Grid container spacing={2}>
                {tagsUnderSelectedOrganization?.map((tag) => {
                  return (
                    <Grid key={tag?.org_tag_id} item md={6} lg={4} xl={3}>
                      <FormControlLabel
                        label={tag?.title}
                        control={
                          <Checkbox
                            checked={selectedTags?.includes(tag?.org_tag_id)}
                            onChange={(_, checked) => {
                              let newSelectedTags = [...selectedTags];

                              if (checked) {
                                newSelectedTags.push(tag?.org_tag_id);
                              } else {
                                newSelectedTags = newSelectedTags?.filter(
                                  (tagId) => tagId !== tag?.org_tag_id
                                );
                              }

                              setSelectedTags(newSelectedTags);
                            }}
                          />
                        }
                      />
                    </Grid>
                  );
                })}
              </Grid>
              {selectedTags?.length > 0 && (
                <Toolbar sx={{ justifyContent: 'flex-end', mt: 2 }}>
                  <Button variant={'contained'} color={'error'} onClick={onDeleteTags}>
                    Delete Tags
                  </Button>
                </Toolbar>
              )}
            </CardContent>
          </Card>
          {bulkAddOpen && (
            <BulkAddTagsDialog
              organizationId={selectedOrganization}
              existingTitles={tagsUnderSelectedOrganization?.map((tag) => tag?.title ?? '') ?? []}
              onClose={() => setBulkAddOpen(false)}
              onAdded={refetchOrgSummaryTags}
            />
          )}
        </>
      ) : null}
    </Container>
  );
}
